import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { CustomerDetail } from './customerApi'

dayjs.extend(advancedFormat)
dayjs.extend(customParseFormat)

export const defaultCustomerDetail = (): CustomerDetail => ({
  id: 0,
  url: '',
  displayName: '',
  displayAs: 0,
  isActive: true,
  isCustomer: true,
  emails: [],
  phones: [],
  properties: 0,
  attachments: 0,
  projects: 0,
})

export const getValidCustomerId = (hash: string): number | null => {
  if (!hash) {
    return null;
  }

  const id = parseInt(hash.split('-')[0]);
  return isNaN(id) ? null : id;
};

export const CONSULT_DATE_FORMAT = 'M/DD/YYYY h:mm A'
export const DEFAULT_STATE_LABEL = 'State / Province / Region'

export type CannedResponse = {
  id: number
  eventDescription?: string | null
  emailBody?: string | null
  emailSubject?: string | null
}

export type ConsultAutoFillCustomer = {
  displayName: string
  address1?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  phones: { number?: string | null }[]
  emails: { address?: string | null }[]
}

export type ConsultAutoFillInput = {
  consultationDate?: string | null
  cannedResponse?: string | null
  projectId?: number | null
  selectedSalesLeads?: Record<string, boolean> | null
}

export const escapeHtml = (value: string): string => {
  if (value.length < 1) {
    return value
  }

  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

export const findCannedResponse = (
  cannedResponses: CannedResponse[] | null | undefined,
  cannedResponseId: string | null | undefined
): CannedResponse | undefined => {
  if (!cannedResponseId || !cannedResponses) {
    return undefined
  }

  return cannedResponses.find(c => c.id.toString() === cannedResponseId)
}

export const parseSalesLeadsToMap = (salesLead: string | null | undefined): Record<string, boolean> => {
  const map: Record<string, boolean> = {}
  if (!salesLead) {
    return map
  }

  salesLead.split(', ').forEach(value => {
    map[value] = true
  })
  return map
}

export const selectedSalesLeadsToString = (
  selectedSalesLeads: Record<string, boolean> | null | undefined,
  separator = ', '
): string => {
  if (!selectedSalesLeads) {
    return ''
  }

  return Object.keys(selectedSalesLeads)
    .filter(key => selectedSalesLeads[key])
    .join(separator)
}

export const tagsToString = (tagsArray: { text: string }[] | null | undefined): string => {
  if (!tagsArray) {
    return ''
  }

  return tagsArray.map(t => t.text).join(', ')
}

export const buildConsultEventDescription = (params: {
  customer: ConsultAutoFillCustomer
  consult: ConsultAutoFillInput
  organizerName: string
  projectName: string
  cannedResponses?: CannedResponse[] | null
}): string => {
  const { customer, consult, organizerName, projectName, cannedResponses } = params

  const dt = dayjs(consult.consultationDate ?? '', CONSULT_DATE_FORMAT)
  const customAddress = [customer.address1, customer.city, customer.state, customer.zip]
    .filter(Boolean)
    .join(', ')
  const salesLeads = consult.selectedSalesLeads
    ? selectedSalesLeadsToString(consult.selectedSalesLeads)
    : ''

  let description = ''

  const canned = findCannedResponse(cannedResponses, consult.cannedResponse)
  if (canned) {
    description += (canned.eventDescription ?? '') + '\n\n'
  }

  description += 'Your '
  description += projectName ? `${projectName} ` : ''
  description += 'consultation has been scheduled'
  description += organizerName ? ` with ${organizerName}` : ''
  description += dt.isValid() ? ` for ${dt.format('dddd, MMMM Do')} at ${dt.format('h:mm A')}.` : '.'
  description += `\n\n Client Name: ${customer.displayName}`
  description += customer.phones.length ? `\n Phone Number(s): ${customer.phones.map(p => p.number).join('; ')}` : ''
  description += customer.emails.length ? `\n Email(s): ${customer.emails.map(e => e.address).join('; ')}` : ''
  description += customAddress ? `\n Address: ${customAddress}` : ''
  description += consult.selectedSalesLeads ? `\n Sales leads: ${salesLeads}` : ''

  return description
}

export const buildConsultEmailBody = (params: {
  customerDisplayName: string
  consult: ConsultAutoFillInput
  organizerName: string
  cannedResponses?: CannedResponse[] | null
}): string => {
  const { customerDisplayName, consult, organizerName, cannedResponses } = params

  let body = `<p>Hi ${escapeHtml(customerDisplayName)},</p>`

  if (consult.cannedResponse) {
    const canned = findCannedResponse(cannedResponses, consult.cannedResponse)
    body += canned?.emailBody ?? ''
  } else {
    body += '<p>Thanks for reaching out to us regarding your project! We’re excited to kick this off and explore what we can do to achieve your vision.</p>'
    body += '<p>Please have all decision makers present for the meeting. We always recommend to take some time before the meeting, think about all the things that you want us to know, and be ready to discuss those questions or options during the consultation. Our goal is to help you achieve your vision and go beyond your expectations!</p>'
    body += '<p>Looking forward to it!</p>'
    body += `<p>Regards, <br/> ${escapeHtml(organizerName)} </p>`
    body += '<p>If you discover you need to reschedule for some reason, please give us as much notice as possible prior to the scheduled meeting.</p>'
  }

  return body
}

export const buildConsultSummary = (params: {
  consult: ConsultAutoFillInput
  projectName: string
  cannedResponses?: CannedResponse[] | null
}): string => {
  const { consult, projectName, cannedResponses } = params

  const canned = findCannedResponse(cannedResponses, consult.cannedResponse)
  if (canned) {
    return canned.emailSubject ?? ''
  }

  return consult.projectId ? `${projectName} Consultation` : 'Scheduled Project Consultation'
}

export const getPrimaryContacts = <T extends { isPrimary?: boolean }>(
  contacts: T[] | null | undefined
): T[] | undefined => {
  if (!contacts || contacts.length === 0) {
    return undefined
  }

  const primary = contacts.filter(c => c.isPrimary)
  if (primary.length === 0) {
    return [contacts[0]]
  }

  return primary
}

export const createNewPhone = (customerId: number) => ({ id: 0, customerId, type: 2 })

export const createNewEmail = (customerId: number) => ({ id: 0, customerId })

export type SinceAtSwitch = {
  customerSinceAtSwitch: string | null
  leadSinceAtSwitch: string | null
}

export const deriveSinceAtSwitch = (
  customer: {
    isCustomer?: boolean
    customerSince?: string | null
    customCustomerSince?: string | null
    leadSince?: string | null
    customLeadSince?: string | null
  },
  nowFormatted: string
): SinceAtSwitch => {
  if (customer.isCustomer && !customer.customerSince && !customer.customCustomerSince) {
    return { customerSinceAtSwitch: nowFormatted, leadSinceAtSwitch: null }
  }

  if (!customer.isCustomer && !customer.leadSince && !customer.customLeadSince) {
    return { customerSinceAtSwitch: null, leadSinceAtSwitch: nowFormatted }
  }

  return { customerSinceAtSwitch: null, leadSinceAtSwitch: null }
}

export type Country = { code: string; hasStates: boolean; stateLabel: string }

export const deriveStateSelection = (
  countries: Country[],
  countryCode: string | null | undefined
): { selectStates: boolean; stateLabel: string } => {
  const country = countries.find(c => c.code === countryCode)
  if (country?.hasStates) {
    return { selectStates: true, stateLabel: country.stateLabel }
  }

  return { selectStates: false, stateLabel: DEFAULT_STATE_LABEL }
}

export type PlaceAddressComponent = {
  types: string[]
  long_name: string
  short_name: string
}

export const getAddressComponent = (
  components: PlaceAddressComponent[] | null | undefined,
  type: string,
  longName = false
): string => {
  if (!components) {
    return ''
  }

  const component = components.find(c => c.types.indexOf(type) >= 0)
  if (!component) {
    return ''
  }

  return longName ? component.long_name : component.short_name
}

export const mapPlaceComponentsToAddress = (
  components: PlaceAddressComponent[] | null | undefined,
  countries: Country[]
): { address1: string; city: string; zip: string; county: string; countryCode: string; state: string } => {
  const countryCode = getAddressComponent(components, 'country')
  const useStateLongName = countries.filter(c => c.code === countryCode && c.hasStates).length === 0

  return {
    address1: (getAddressComponent(components, 'street_number', true) + ' ' + getAddressComponent(components, 'route', true)).trim(),
    city: getAddressComponent(components, 'locality', true) || getAddressComponent(components, 'sublocality', true),
    zip: getAddressComponent(components, 'postal_code'),
    county: getAddressComponent(components, 'administrative_area_level_2', true),
    countryCode,
    state: getAddressComponent(components, 'administrative_area_level_1', useStateLongName),
  }
}

export type InviteAddress = { address: string; selected: boolean }

export const buildInviteAddresses = (params: {
  organizerEmail: string
  customerEmails: { address: string; isPrimary?: boolean }[]
  createdBy?: string | null
}): { addresses: InviteAddress[]; replyToAddresses: InviteAddress[] } => {
  const { organizerEmail, customerEmails, createdBy } = params

  const addresses: InviteAddress[] = [{ address: organizerEmail, selected: true }]
  customerEmails.forEach(e => addresses.push({ address: e.address, selected: e.isPrimary === true }))

  const replyToAddresses: InviteAddress[] = [{ address: organizerEmail, selected: true }]
  customerEmails.forEach(e => replyToAddresses.push({ address: e.address, selected: e.isPrimary === true }))
  if (createdBy) {
    replyToAddresses.push({ address: createdBy, selected: false })
  }

  return { addresses, replyToAddresses }
}

export const buildSendInvitesPayload = (invite: {
  addresses: InviteAddress[]
  replyToAddresses: InviteAddress[]
}): { addresses: string[]; replyToAddresses: string[] } => ({
  addresses: invite.addresses.filter(e => e.selected).map(e => e.address),
  replyToAddresses: invite.replyToAddresses.filter(e => e.selected).map(e => e.address),
})
